import { useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import DatabaseCheckPanel from '../components/DatabaseCheckPanel'
import { schemaTemplatesApi, type SchemaMasterConfig } from '../api/schemaTemplates'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  CircleStackIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PlusCircleIcon,
  ServerStackIcon,
  ShieldExclamationIcon,
  StarIcon as StarOutlineIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface Template {
  filename: string
  size: number
  modifiedAt: string
  isDefault: boolean
  header: string[]
}

interface Company {
  id: string
  company_name: string
  company_code: string
  database_name: string
  database_host?: string
  database_port?: string
}

interface SeedResult {
  table: string
  rows: number
  skipped?: string
}

interface DumpResult {
  sourceDatabase: string
  sourceHost: string
  tablesDumped: number
  /** Views and triggers are part of the schema too — the dump used to fetch
   *  only BASE TABLE, so tenants provisioned from the template silently
   *  lacked them. Surfaced here so an omission is visible, not assumed. */
  viewsDumped?: number
  triggersDumped?: number
  seedResults: SeedResult[]
  sizeBytes: number
  savedFilename?: string
}

const DEFAULT_SEED_TABLES = [
  'permissions',
  'role_permissions',
  'cost_categories',
  'cost_templates',
  'cost_template_sections',
  'cost_template_items',
]

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

/** See Backups.tsx — `embedded` drops the standalone page header when this
 *  renders inside the Database section's Schema tab. */
export default function SchemaTemplates({ embedded = false }: { embedded?: boolean }) {
  const [templates, setTemplates] = useState<Template[]>([])
  const [defaultFilename, setDefaultFilename] = useState<string>('tenant-schema.sql')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Set when a refresh was refused because the source lacks columns the
  // current template has (409 TEMPLATE_WOULD_LOSE_COLUMNS): repeats it anyway.
  const [replaceAnyway, setReplaceAnyway] = useState<null | (() => Promise<void>)>(null)
  const [success, setSuccess] = useState('')

  const [companies, setCompanies] = useState<Company[]>([])
  const [dumpOpen, setDumpOpen] = useState(false)
  const [dumpSubmitting, setDumpSubmitting] = useState(false)

  const [sourceType, setSourceType] = useState<'company' | 'raw'>('company')
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [rawHost, setRawHost] = useState('')
  const [rawPort, setRawPort] = useState('3306')
  const [rawDbName, setRawDbName] = useState('devdb2')
  const [rawUser, setRawUser] = useState('')
  const [rawPassword, setRawPassword] = useState('')

  const [seedTablesText, setSeedTablesText] = useState(DEFAULT_SEED_TABLES.join('\n'))
  const [excludeTablesText, setExcludeTablesText] = useState('')
  const [setAsDefault, setSetAsDefault] = useState(true)
  const [customFilename, setCustomFilename] = useState('')
  const [lastResult, setLastResult] = useState<DumpResult | null>(null)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewFilename, setPreviewFilename] = useState('')
  const [previewSql, setPreviewSql] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  type SortKey = 'modified' | 'name' | 'size'
  const [sortKey, setSortKey] = useState<SortKey>('modified')

  const [master, setMaster] = useState<SchemaMasterConfig | null>(null)
  const [masterSaving, setMasterSaving] = useState(false)

  const [hostFixCompanyId, setHostFixCompanyId] = useState('')
  const [hostFixHost, setHostFixHost] = useState('')
  const [hostFixPort, setHostFixPort] = useState('')
  const [hostFixSaving, setHostFixSaving] = useState(false)

  const loadMaster = async () => {
    try {
      setMaster(await schemaTemplatesApi.getMaster())
    } catch (e: any) {
      // non-fatal — keep page usable even if endpoint returns 500
    }
  }

  useEffect(() => {
    loadMaster()
  }, [])

  const handleSetMaster = async (companyId: string | null) => {
    setMasterSaving(true)
    try {
      await schemaTemplatesApi.setMaster(companyId)
      await loadMaster()
      await loadTemplates()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update master')
    } finally {
      setMasterSaving(false)
    }
  }

  useEffect(() => {
    loadTemplates()
    loadCompanies()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/super-admin/schema-templates')
      setTemplates(data.templates || [])
      setDefaultFilename(data.defaultFilename || 'tenant-schema.sql')
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  const loadCompanies = async () => {
    try {
      const { data } = await api.get('/super-admin/companies')
      const list: Company[] = Array.isArray(data) ? data : data.companies || []
      setCompanies(list)
    } catch {
      // non-fatal
    }
  }

  const resetDumpForm = () => {
    setSourceType('company')
    setSelectedCompanyId('')
    setRawHost('')
    setRawPort('3306')
    setRawDbName('devdb2')
    setRawUser('')
    setRawPassword('')
    setSeedTablesText(DEFAULT_SEED_TABLES.join('\n'))
    setExcludeTablesText('')
    setSetAsDefault(true)
    setCustomFilename('')
    setLastResult(null)
  }

  const openDumpModal = () => {
    resetDumpForm()
    setDumpOpen(true)
  }

  const submitDump = async (allowLosses = false) => {
    setError('')
    setReplaceAnyway(null)
    setSuccess('')
    setLastResult(null)

    const seedTables = seedTablesText
      .split(/\n|,/)
      .map(s => s.trim())
      .filter(Boolean)
    const excludeTables = excludeTablesText
      .split(/\n|,/)
      .map(s => s.trim())
      .filter(Boolean)

    const payload: Record<string, unknown> = {
      sourceType,
      seedTables,
      excludeTables,
      setAsDefault,
      saveAs: setAsDefault ? undefined : customFilename.trim() || undefined,
    }

    if (sourceType === 'company') {
      if (!selectedCompanyId) {
        setError('Select a source company')
        return
      }
      payload.companyId = selectedCompanyId
    } else {
      if (!rawDbName.trim()) {
        setError('Database name is required')
        return
      }
      payload.dbHost = rawHost.trim() || undefined
      payload.dbPort = rawPort.trim() || undefined
      payload.dbName = rawDbName.trim()
      payload.dbUser = rawUser.trim() || undefined
      payload.dbPassword = rawPassword || undefined
    }

    if (!setAsDefault && !customFilename.trim()) {
      setError('Provide a filename or enable "Set as default"')
      return
    }

    try {
      setDumpSubmitting(true)
      const { data } = await api.post('/super-admin/schema-templates/dump', { ...payload, allowLosses })
      setLastResult({
        sourceDatabase: data.sourceDatabase,
        sourceHost: data.sourceHost,
        tablesDumped: data.tablesDumped,
        viewsDumped: data.viewsDumped,
        triggersDumped: data.triggersDumped,
        seedResults: data.seedResults || [],
        sizeBytes: data.sizeBytes,
        savedFilename: data.savedFilename,
      })
      setSuccess(`Template saved: ${data.savedFilename}`)
      await loadTemplates()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Dump failed')
      if (e.response?.data?.code === 'TEMPLATE_WOULD_LOSE_COLUMNS') {
        setReplaceAnyway(() => () => submitDump(true))
      }
    } finally {
      setDumpSubmitting(false)
    }
  }

  const openPreview = async (filename: string) => {
    setPreviewOpen(true)
    setPreviewFilename(filename)
    setPreviewSql('')
    setPreviewLoading(true)
    try {
      const { data } = await api.get(`/super-admin/schema-templates/${encodeURIComponent(filename)}/content`)
      setPreviewSql(data.sql || '')
    } catch (e: any) {
      setPreviewSql(`-- Failed to load: ${e.response?.data?.message || e.message}`)
    } finally {
      setPreviewLoading(false)
    }
  }

  const downloadTemplate = async (filename: string) => {
    try {
      const { data } = await api.get(`/super-admin/schema-templates/${encodeURIComponent(filename)}/content`)
      const blob = new Blob([data.sql || ''], { type: 'application/sql' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Download failed')
    }
  }

  const deleteTemplate = async (filename: string) => {
    if (!confirm(`Delete template "${filename}"? This cannot be undone.`)) return
    try {
      await api.delete(`/super-admin/schema-templates/${encodeURIComponent(filename)}`)
      setSuccess(`Deleted ${filename}`)
      await loadTemplates()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Delete failed')
    }
  }

  const selectedCompany = useMemo(
    () => companies.find(c => c.id === selectedCompanyId),
    [companies, selectedCompanyId]
  )

  const hostFixCompany = useMemo(
    () => companies.find(c => c.id === hostFixCompanyId),
    [companies, hostFixCompanyId]
  )

  const selectHostFixCompany = (companyId: string) => {
    setHostFixCompanyId(companyId)
    const c = companies.find(co => co.id === companyId)
    setHostFixHost(c?.database_host || '')
    setHostFixPort(c?.database_port || '')
  }

  const saveHostFix = async () => {
    if (!hostFixCompanyId) return
    setError('')
    setSuccess('')
    try {
      setHostFixSaving(true)
      await api.put(`/super-admin/companies/${hostFixCompanyId}`, {
        database_host: hostFixHost.trim(),
        database_port: hostFixPort.trim() || undefined,
      })
      setSuccess(`Updated database host for ${hostFixCompany?.company_name || 'company'}.`)
      await loadCompanies()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update database host')
    } finally {
      setHostFixSaving(false)
    }
  }

  const sortedTemplates = useMemo(() => {
    const copy = [...templates]
    copy.sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1
      switch (sortKey) {
        case 'name': return a.filename.localeCompare(b.filename)
        case 'size': return b.size - a.size
        case 'modified':
        default: return new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
      }
    })
    return copy
  }, [templates, sortKey])

  return (
    <div className={embedded ? '' : 'p-6 max-w-6xl mx-auto'}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          {!embedded && (
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CircleStackIcon className="w-7 h-7 text-blue-600" />
              Schema Templates
            </h1>
          )}
          <p className="text-sm text-slate-500 mt-1">
            Stored SQL templates used to provision new tenant databases. Pull a fresh copy from any existing tenant
            or reference DB whenever the schema changes.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadTemplates}
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={openDumpModal}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm flex items-center gap-2"
          >
            <PlusCircleIcon className="w-4 h-4" />
            Pull fresh schema
          </button>
        </div>
      </div>

      {/* Master Tenant card */}
      <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <StarOutlineIcon className="w-5 h-5 text-amber-500" />
            <div>
              <h2 className="text-sm font-semibold text-slate-700">Master tenant</h2>
              <p className="text-xs text-slate-500">
                Schema templates auto-refresh after successful migrations applied to this tenant.
              </p>
            </div>
          </div>
          {master?.hasMigrationsNewerThanTemplate && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded">
              <ShieldExclamationIcon className="w-3.5 h-3.5" />
              Master has migrations newer than the template
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <select
            value={master?.companyId ?? ''}
            onChange={(e) => handleSetMaster(e.target.value || null)}
            disabled={masterSaving}
            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— No master tenant set —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name} ({c.company_code}) — {c.database_name}
              </option>
            ))}
          </select>
          {master?.companyId && (
            <button
              onClick={async () => {
                const redump = async (allowLosses: boolean) => {
                  setMasterSaving(true)
                  setError('')
                  setReplaceAnyway(null)
                  try {
                    await api.post('/super-admin/schema-templates/dump', {
                      sourceType: 'company',
                      companyId: master.companyId,
                      setAsDefault: true,
                      allowLosses,
                    })
                    await loadTemplates()
                    await loadMaster()
                    setSuccess('Re-dumped template from master tenant.')
                  } catch (e: any) {
                    setError(e.response?.data?.message || 'Re-dump failed')
                    if (e.response?.data?.code === 'TEMPLATE_WOULD_LOSE_COLUMNS') {
                      setReplaceAnyway(() => () => redump(true))
                    }
                  } finally {
                    setMasterSaving(false)
                  }
                }
                await redump(false)
              }}
              disabled={masterSaving}
              className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Re-dump now
            </button>
          )}
        </div>
        {master?.companyId && master?.lastDumpAt && (
          <p className="mt-2 text-xs text-slate-500">
            Last template-impacting migration on master: {new Date(master.lastDumpAt).toLocaleString()}
          </p>
        )}
      </div>

      <DatabaseCheckPanel companies={companies} />

      {/* Fix a tenant's database host card */}
      <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-sm font-semibold text-slate-700">Fix tenant database host</h2>
            <p className="text-xs text-slate-500">
              Pick a tenant to view or correct its stored <code>database_host</code>/<code>database_port</code> —
              useful when a dump/migration fails with a DNS or connection error against a stale host.
            </p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1fr_auto] gap-3 items-center">
          <select
            value={hostFixCompanyId}
            onChange={(e) => selectHostFixCompany(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Select a tenant —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name} ({c.company_code}) — {c.database_name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={hostFixHost}
            onChange={(e) => setHostFixHost(e.target.value)}
            placeholder="database host"
            disabled={!hostFixCompanyId}
            className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
          />
          <input
            type="text"
            value={hostFixPort}
            onChange={(e) => setHostFixPort(e.target.value)}
            placeholder="port"
            disabled={!hostFixCompanyId}
            className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
          />
          <button
            onClick={saveHostFix}
            disabled={!hostFixCompanyId || !hostFixHost.trim() || hostFixSaving}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
          >
            {hostFixSaving ? 'Saving…' : 'Save host'}
          </button>
        </div>
        {hostFixCompany && (
          <p className="mt-2 text-xs text-slate-500">
            Currently stored: <span className="font-mono">{hostFixCompany.database_host || '(none — falls back to app DB_HOST env)'}</span>
            {hostFixCompany.database_port ? `:${hostFixCompany.database_port}` : ''}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <ShieldExclamationIcon className="w-5 h-5 flex-shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
          {replaceAnyway && (
            <button
              onClick={() => replaceAnyway()}
              className="flex-shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-100"
            >
              Replace anyway
            </button>
          )}
          <button onClick={() => { setError(''); setReplaceAnyway(null) }} className="ml-auto text-red-400 hover:text-red-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-start gap-2">
          <CheckBadgeIcon className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-emerald-400 hover:text-emerald-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-slate-700">
            Stored templates <span className="text-xs font-normal text-slate-400">({templates.length})</span>
          </h2>
          {templates.length > 1 && (
            <label className="flex items-center gap-2 text-xs text-slate-500">
              Sort by
              <select
                value={sortKey}
                onChange={e => setSortKey(e.target.value as SortKey)}
                className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="modified">Last modified</option>
                <option value="name">Name (A–Z)</option>
                <option value="size">Size (largest first)</option>
              </select>
            </label>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No templates yet. Click "Pull fresh schema" to create one.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedTemplates.map(t => (
              <div key={t.filename} className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-slate-900 truncate">{t.filename}</span>
                    {t.isDefault && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                        Default
                      </span>
                    )}
                    {t.isDefault && master?.companyName && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 inline-flex items-center gap-1">
                        <DocumentCheckIcon className="w-3 h-3" />
                        Auto-refreshed from {master.companyName}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {formatBytes(t.size)} · updated {new Date(t.modifiedAt).toLocaleString()}
                  </div>
                  {t.header.length > 0 && (
                    <pre className="mt-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded p-2 whitespace-pre-wrap">
                      {t.header.join('\n')}
                    </pre>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openPreview(t.filename)}
                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Preview"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadTemplate(t.filename)}
                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                  {!t.isDefault && (
                    <button
                      onClick={() => deleteTemplate(t.filename)}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dump Modal */}
      {dumpOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => !dumpSubmitting && setDumpOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl my-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <ServerStackIcon className="w-5 h-5 text-blue-600" />
                Pull schema → Save as template
              </h2>
              <button
                onClick={() => !dumpSubmitting && setDumpOpen(false)}
                className="p-1 rounded hover:bg-slate-100"
                disabled={dumpSubmitting}
              >
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  Source
                </label>
                <div className="flex rounded-lg border border-slate-300 overflow-hidden w-fit text-sm">
                  <button
                    type="button"
                    onClick={() => setSourceType('company')}
                    className={`px-3 py-1.5 ${sourceType === 'company' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Existing tenant
                  </button>
                  <button
                    type="button"
                    onClick={() => setSourceType('raw')}
                    className={`px-3 py-1.5 border-l border-slate-300 ${sourceType === 'raw' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    Raw DB (e.g. devdb2)
                  </button>
                </div>
              </div>

              {sourceType === 'company' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Source company</label>
                  <select
                    value={selectedCompanyId}
                    onChange={e => setSelectedCompanyId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a company…</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.company_name} ({c.company_code}) — {c.database_name}
                      </option>
                    ))}
                  </select>
                  {selectedCompany && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      Will connect to <span className="font-mono">{selectedCompany.database_host || 'default host'}:{selectedCompany.database_port || '3306'}</span> DB <span className="font-mono">{selectedCompany.database_name}</span> using the company's stored super-admin credentials.
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database name *</label>
                    <input
                      value={rawDbName}
                      onChange={e => setRawDbName(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      placeholder="devdb2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Host</label>
                    <input
                      value={rawHost}
                      onChange={e => setRawHost(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      placeholder="server env default"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Port</label>
                    <input
                      value={rawPort}
                      onChange={e => setRawPort(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">User</label>
                    <input
                      value={rawUser}
                      onChange={e => setRawUser(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      placeholder="server env default"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      value={rawPassword}
                      onChange={e => setRawPassword(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      placeholder="server env default"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Seed tables (one per line)</label>
                  <textarea
                    rows={5}
                    value={seedTablesText}
                    onChange={e => setSeedTablesText(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Rows from these tables are included as INSERTs.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Exclude tables (optional)</label>
                  <textarea
                    rows={5}
                    value={excludeTablesText}
                    onChange={e => setExcludeTablesText(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono"
                    placeholder="e.g. migrations_applied"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">DDL skipped for these tables.</p>
                </div>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={setAsDefault}
                    onChange={e => setSetAsDefault(e.target.checked)}
                  />
                  Set as default template (<span className="font-mono">{defaultFilename}</span>)
                </label>
                {!setAsDefault && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Save as filename</label>
                    <input
                      value={customFilename}
                      onChange={e => setCustomFilename(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      placeholder="tenant-schema-v2.sql"
                    />
                  </div>
                )}
              </div>

              {lastResult && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                  <div className="font-semibold mb-1 flex items-center gap-1.5"><CheckBadgeIcon className="w-4 h-4" /> Saved {lastResult.savedFilename}</div>
                  <div>
                    Source: <span className="font-mono">{lastResult.sourceHost}/{lastResult.sourceDatabase}</span> ·{' '}
                    {lastResult.tablesDumped} tables
                    {lastResult.viewsDumped ? ` · ${lastResult.viewsDumped} views` : ''}
                    {lastResult.triggersDumped ? ` · ${lastResult.triggersDumped} triggers` : ''}
                    {' · '}{formatBytes(lastResult.sizeBytes)}
                  </div>
                  {lastResult.seedResults.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">Seed rows</summary>
                      <ul className="mt-1 pl-4 list-disc">
                        {lastResult.seedResults.map(s => (
                          <li key={s.table}>
                            <span className="font-mono">{s.table}</span>: {s.rows}
                            {s.skipped ? ` (skipped: ${s.skipped})` : ''}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setDumpOpen(false)}
                disabled={dumpSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={() => submitDump()}
                disabled={dumpSubmitting}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm disabled:opacity-50 flex items-center gap-2"
              >
                {dumpSubmitting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Dumping…
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Dump &amp; Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-4xl my-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 font-mono">{previewFilename}</h2>
              <button onClick={() => setPreviewOpen(false)} className="p-1 rounded hover:bg-slate-100">
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto bg-slate-900">
              {previewLoading ? (
                <div className="text-center text-slate-400 py-8">Loading…</div>
              ) : (
                <pre className="text-[11px] text-slate-100 font-mono whitespace-pre">{previewSql}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
